import { useEffect, useState, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";

export interface Risk {
  id: string;
  site_id: string | null;
  title: string;
  description: string | null;
  category: string;
  severity: string;
  likelihood: number;
  impact: number;
  risk_score: number;
  status: string;
  mitigation_plan: string | null;
  mitigation_status: string;
  mitigation_deadline: string | null;
  assigned_to: string | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

export interface RiskComment {
  id: string;
  risk_id: string;
  user_id: string;
  user_name: string | null;
  user_role: string | null;
  content: string;
  created_at: string;
}

export const useRiskData = (siteIds?: string[]) => {
  const [risks, setRisks] = useState<Risk[]>([]);
  const [comments, setComments] = useState<RiskComment[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchRisks = useCallback(async () => {
    let query = supabase.from("risks").select("*").order("created_at", { ascending: false });
    if (siteIds && siteIds.length > 0) {
      query = query.in("site_id", siteIds);
    }
    const { data } = await query;
    if (data) setRisks(data as unknown as Risk[]);
  }, [siteIds?.join(",")]);

  const fetchComments = useCallback(async (riskIds?: string[]) => {
    if (!riskIds || riskIds.length === 0) {
      setComments([]);
      return;
    }
    const { data } = await supabase
      .from("risk_comments")
      .select("*")
      .in("risk_id", riskIds)
      .order("created_at", { ascending: true });
    if (data) setComments(data as unknown as RiskComment[]);
  }, []);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      await fetchRisks();
      setLoading(false);
    };
    load();
  }, [fetchRisks]);

  useEffect(() => {
    if (risks.length > 0) {
      fetchComments(risks.map((r) => r.id));
    }
  }, [risks, fetchComments]);

  // Realtime
  useEffect(() => {
    const channel = supabase
      .channel("risks-realtime")
      .on("postgres_changes", { event: "*", schema: "public", table: "risks" }, () => fetchRisks())
      .on("postgres_changes", { event: "*", schema: "public", table: "risk_comments" }, () => {
        if (risks.length > 0) fetchComments(risks.map((r) => r.id));
      })
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [fetchRisks, risks]);

  const addRisk = async (risk: Partial<Risk>) => {
    const { error } = await supabase.from("risks").insert(risk as any);
    if (error) throw error;
    await fetchRisks();
  };

  const updateRisk = async (id: string, updates: Partial<Risk>) => {
    const { error } = await supabase.from("risks").update(updates as any).eq("id", id);
    if (error) throw error;
    await fetchRisks();
  };

  const deleteRisk = async (id: string) => {
    const { error } = await supabase.from("risks").delete().eq("id", id);
    if (error) throw error;
    await fetchRisks();
  };

  const addComment = async (comment: { risk_id: string; user_id: string; user_name: string; user_role: string; content: string }) => {
    const { error } = await supabase.from("risk_comments").insert(comment as any);
    if (error) throw error;
    await fetchComments(risks.map((r) => r.id));
  };

  return { risks, comments, loading, addRisk, updateRisk, deleteRisk, addComment, refetch: fetchRisks };
};
